import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { API } from "@/lib/api";
import { useTaal } from "@/contexts/TaalContext";
import { Button } from "./ui/button";
import { Input } from "./ui/input";

/** Glasronde 0.9.244 punt 2 (besluit Frank, 4 nov): regels en voorstellen die niet in de taal van de app staan.
 *
 *  Eerst "Vertaal": het lokale model vertaalt, en er wordt NIETS geschreven. Frank ziet origineel en vertaling naast
 *  elkaar, kan een vertaling verbeteren, en een twijfelgeval staat uit met de reden. Pas "Toepassen" schrijft, en het
 *  origineel blijft als citaat bij de regel.
 */
/** Glasronde 0.9.245: wat er met een regel gebeurt (B, C en D naast het vertalen). */
const AARD = { vertalen: "Vertalen.", herzien: "Vertaalfout verbeteren.", netjes: "Nette zin maken." };

export default function Taalronde({ teller, onKlaar }) {
  const { t } = useTaal();
  const [aantal, setAantal] = useState(0);
  const [items, setItems] = useState(null);
  // B3 deel 2: de ronde is een kaart; toepassen stuurt haar token mee (WF-40).
  const [ronde, setRonde] = useState({ ronde_id: "", token: "" });
  const [bezig, setBezig] = useState(false);

  useEffect(() => {
    let weg = false;
    axios.get(`${API}/memory/taalronde`)
      .then((r) => { if (!weg) setAantal(r.data?.aantal || 0); })
      .catch(() => { if (!weg) setAantal(0); });
    return () => { weg = true; };
  }, [teller]);

  if (!aantal && !items) return null;

  const vertaal = async () => {
    setBezig(true);
    try {
      const r = await axios.post(`${API}/memory/taalronde/voorstel`);
      if (r.data?.reden) {
        toast.error(r.data.reden);
        return;
      }
      setRonde({ ronde_id: r.data?.ronde_id || "", token: r.data?.token || "" });
      setItems((r.data?.items || []).map((i) => ({ ...i, aan: !i.twijfel })));
    } catch (e) {
      toast.error(e.response?.data?.detail || t("Dat lukte niet. Er is niets gewijzigd."));
    } finally {
      setBezig(false);
    }
  };

  const zet = (id, veld, waarde) => setItems((oud) => oud.map((i) => (i.id === id ? { ...i, [veld]: waarde } : i)));

  const pasToe = async () => {
    const gekozen = items.filter((i) => i.aan).map(({ id, soort, aard, origineel, huidig, vertaling }) => ({ id, soort, aard, origineel, huidig, vertaling }));
    setBezig(true);
    try {
      const r = await axios.post(`${API}/memory/taalronde/toepassen`, { items: gekozen, ...ronde });
      const gedaan = (r.data || []).filter((u) => u.gedaan).length;
      toast.success(t("{n} regels vertaald; het origineel staat erbij als citaat.", { n: gedaan }));
      setItems(null);
      if (onKlaar) onKlaar();
    } catch (e) {
      toast.error(e.response?.data?.detail || t("Dat lukte niet. Er is niets gewijzigd."));
    } finally {
      setBezig(false);
    }
  };

  return (
    <section className="border rounded-lg p-3 space-y-3 mb-4" data-testid="taalronde">
      <div>
        <h3 className="text-sm font-medium">{t("Regels om na te lopen")}</h3>
        <p className="text-xs text-muted-foreground">
          {t("{n} regels of voorstellen staan niet in de taal van de app, hebben een vertaalfout of zijn nog geen nette zin. Vertalen gebeurt met je lokale model; er verandert pas iets als je op Toepassen klikt.", { n: aantal })}
        </p>
      </div>
      {!items ? (
        <Button size="sm" variant="outline" disabled={bezig} onClick={vertaal} data-testid="taalronde-vertaal">
          {bezig && <Loader2 className="h-3 w-3 animate-spin mr-1" />}{t("Vertaal")}
        </Button>
      ) : (
        <>
          <ul className="space-y-2">
            {items.map((i) => (
              <li key={i.id} className="border rounded-md p-2 text-sm space-y-1" data-testid={`taalronde-${i.id}`}>
                <label className="flex items-start gap-2">
                  <input type="checkbox" checked={i.aan} onChange={(e) => zet(i.id, "aan", e.target.checked)}
                         data-testid={`taalronde-aan-${i.id}`} />
                  <span className="text-xs text-muted-foreground break-words">
                    <span className="font-medium" data-testid={`taalronde-aard-${i.id}`}>{t(AARD[i.aard] || AARD.vertalen)}</span>{" "}
                    {t("Origineel: {tekst}", { tekst: i.origineel })}
                  </span>
                </label>
                {i.aard === "herzien" && (
                  <p className="text-xs text-muted-foreground break-words" data-testid={`taalronde-nu-${i.id}`}>
                    {t("Nu: {tekst}", { tekst: i.huidig })}
                  </p>
                )}
                <Input value={i.vertaling} onChange={(e) => zet(i.id, "vertaling", e.target.value)}
                       aria-label={t("Vertaling")} className="h-8 text-xs" data-testid={`taalronde-tekst-${i.id}`} />
                {i.twijfel && (
                  <p className="text-xs text-[hsl(var(--destructive))]" data-testid={`taalronde-twijfel-${i.id}`}>
                    {t("Twijfel: {reden}. Lees hem na en vink hem aan als hij klopt.", { reden: i.twijfel })}
                  </p>
                )}
              </li>
            ))}
          </ul>
          <Button size="sm" disabled={bezig || !items.some((i) => i.aan)} onClick={pasToe} data-testid="taalronde-toepassen">
            {t("Toepassen")}
          </Button>
        </>
      )}
    </section>
  );
}
